import i18next, { PostProcessorModule } from 'i18next';
import { useEffect, useState } from 'react';

/**
 * R49-5 Übersetzermodus (Mein Bereich › Übersetzungen): while on, every string i18next hands out
 * for a text key that a text block serves is remembered with that block's key, so the page layer
 * can put an edit icon next to exactly those rendered texts. The state lives in memory only — a
 * reload, a logout or a new login always starts with the mode off. Off, nothing runs: the
 * post-processor is not in i18next's chain and no page layer is mounted.
 */

export const TRANSLATOR_MODE_EVENT = 'translatorModeChanged';
const POST_PROCESSOR_NAME = 'translatorMode';

/** AdminPanel namespaces never take part (the mode belongs to the website and Mein Bereich). */
const EXCLUDED_NAMESPACES = new Set([
    'adminpanel',
    'actions',
    'adminroutes',
    'articleManagement',
    'it',
    'office',
    'offsetting',
    'service',
]);

let enabled = false;
const listeners = new Set<() => void>();

/** "<namespace>:<path>" -> text block key, as the I18NextBackend received it from the templating service. */
const textBlockSources = new Map<string, string>();
/** The static text block map (R38-3), reversed; loaded when the mode is switched on. */
let mappedTextBlocks: Map<string, string> | null = null;
/** Rendered (normalised) text -> text block key, filled while the mode is on. */
const renderedTexts = new Map<string, string>();

export function recordTextBlockSource(namespace: string, path: string, textBlockKey: string): void {
    textBlockSources.set(`${namespace}:${path}`, textBlockKey);
}

export function textBlockKeyOf(namespace: string, path: string): string | undefined {
    const target = `${namespace}:${path}`;
    return mappedTextBlocks?.get(target) ?? textBlockSources.get(target);
}

export function normalizeRenderedText(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}

// two letters at least: separators, numbers and single characters never get an icon
const EDITABLE_TEXT = /[A-Za-zÀ-ɏЀ-ӿ].*[A-Za-zÀ-ɏЀ-ӿ]/;

export function textBlockKeyForRenderedText(text: string): string | undefined {
    return renderedTexts.get(normalizeRenderedText(text));
}

interface ResolvedTranslation {
    res?: unknown;
    usedKey?: string;
    usedNS?: string;
}

export const translatorModePostProcessor: PostProcessorModule = {
    type: 'postProcessor',
    name: POST_PROCESSOR_NAME,
    process(value: string, _key: string | string[], options: { i18nResolved?: ResolvedTranslation }) {
        const resolved = options?.i18nResolved;
        if (!enabled || typeof value !== 'string' || !resolved || typeof resolved.res !== 'string') return value;
        if (!resolved.usedNS || !resolved.usedKey || EXCLUDED_NAMESPACES.has(resolved.usedNS)) return value;
        const textBlockKey = textBlockKeyOf(resolved.usedNS, resolved.usedKey);
        const rendered = normalizeRenderedText(value);
        if (textBlockKey && EDITABLE_TEXT.test(rendered)) renderedTexts.set(rendered, textBlockKey);
        return value;
    },
};

async function loadMappedTextBlocks(): Promise<void> {
    if (mappedTextBlocks) return;
    const map = (await import('src/resources/textblocks/mein_bereich_map.json')).default as Record<string, string[]>;
    const reversed = new Map<string, string>();
    Object.entries(map).forEach(([textBlockKey, targets]) =>
        targets.forEach(target => reversed.set(target, textBlockKey))
    );
    mappedTextBlocks = reversed;
}

export function isTranslatorModeOn(): boolean {
    return enabled;
}

export async function setTranslatorMode(on: boolean): Promise<void> {
    if (on === enabled) return;
    if (on) await loadMappedTextBlocks();
    enabled = on;
    i18next.options.postProcess = on ? [POST_PROCESSOR_NAME] : false;
    i18next.options.postProcessPassResolved = on;
    if (!on) renderedTexts.clear();
    // re-renders every useTranslation consumer (react.bindI18n) so the page texts get recorded
    i18next.emit(TRANSLATOR_MODE_EVENT);
    listeners.forEach(listener => listener());
}

export function useTranslatorMode(): boolean {
    const [on, setOn] = useState(enabled);
    useEffect(() => {
        const listener = () => setOn(enabled);
        listeners.add(listener);
        listener();
        return () => {
            listeners.delete(listener);
        };
    }, []);
    return on;
}
